/**
 * Native-runtime bridge for Capacitor. All calls are safe on the web —
 * every helper feature-detects and no-ops if we are not inside a native shell.
 *
 * Nothing else in the app needs to change: existing components import from here
 * and get web-compatible fallbacks automatically.
 */
import { Capacitor } from "@capacitor/core";

export const isNative = () => Capacitor.isNativePlatform && Capacitor.isNativePlatform();
export const platform = () => Capacitor.getPlatform();

/** Hardware back button — pushes into React Router history. */
export async function wireHardwareBack(navigate) {
  if (!isNative()) return () => {};
  try {
    const { App } = await import("@capacitor/app");
    const handle = await App.addListener("backButton", ({ canGoBack }) => {
      if (canGoBack) {
        // Prefer history.back for accuracy across nested routes.
        window.history.back();
      } else {
        App.exitApp();
      }
    });
    return () => handle.remove();
  } catch (err) {
    console.debug("back button wire failed", err);
    return () => {};
  }
}

/** Splash + status bar tuning on cold start. */
export async function setupChrome() {
  if (!isNative()) return;
  try {
    const { StatusBar, Style } = await import("@capacitor/status-bar");
    await StatusBar.setStyle({ style: Style.Dark });
    await StatusBar.setBackgroundColor({ color: "#14532D" });
  } catch (err) { console.debug("statusbar", err); }
  try {
    const { SplashScreen } = await import("@capacitor/splash-screen");
    await SplashScreen.hide();
  } catch (err) { console.debug("splash", err); }
}

/** Native share sheet — falls back to wa.me / navigator.share on web. */
export async function nativeShare({ title, text, url, dialogTitle }) {
  if (isNative()) {
    try {
      const { Share } = await import("@capacitor/share");
      await Share.share({ title, text, url, dialogTitle: dialogTitle || "Share" });
      return true;
    } catch (err) {
      console.debug("native share failed", err);
    }
  }
  if (navigator.share) {
    try { await navigator.share({ title, text, url }); return true; } catch (err) { console.debug(err); }
  }
  return false;
}

/** Native photo capture — returns dataURL. Web callers keep <input type=file capture>. */
export async function nativeCapture() {
  if (!isNative()) return null;
  try {
    const { Camera, CameraResultType, CameraSource } = await import("@capacitor/camera");
    const photo = await Camera.getPhoto({
      quality: 70,
      allowEditing: false,
      resultType: CameraResultType.DataUrl,
      source: CameraSource.Camera,
    });
    return photo.dataUrl || null;
  } catch (err) {
    console.debug("camera failed", err);
    return null;
  }
}

/** Save a PDF/blob to the device Downloads folder. Returns the file URI. */
export async function nativeSavePdf(base64Data, filename) {
  if (!isNative()) return null;
  try {
    const { Filesystem, Directory } = await import("@capacitor/filesystem");
    const res = await Filesystem.writeFile({
      path: filename,
      data: base64Data,
      directory: Directory.Documents,
      recursive: true,
    });
    return res.uri;
  } catch (err) {
    console.debug("save pdf failed", err);
    return null;
  }
}

/** Online/offline banner state. */
export async function watchNetwork(setOnline) {
  if (!isNative()) {
    const upd = () => setOnline(navigator.onLine);
    window.addEventListener("online", upd);
    window.addEventListener("offline", upd);
    upd();
    return () => {
      window.removeEventListener("online", upd);
      window.removeEventListener("offline", upd);
    };
  }
  try {
    const { Network } = await import("@capacitor/network");
    const status = await Network.getStatus();
    setOnline(status.connected);
    const handle = await Network.addListener("networkStatusChange", (s) => setOnline(s.connected));
    return () => handle.remove();
  } catch (err) {
    console.debug("network", err);
    return () => {};
  }
}

/** Register a Firebase push token once the app is native. Emits the token so the caller can POST it upstream. */
export async function registerPush(onToken) {
  if (!isNative()) return () => {};
  try {
    const { PushNotifications } = await import("@capacitor/push-notifications");
    const perm = await PushNotifications.requestPermissions();
    if (perm.receive !== "granted") return () => {};
    await PushNotifications.register();
    const h1 = await PushNotifications.addListener("registration", (t) => onToken && onToken(t.value));
    const h2 = await PushNotifications.addListener("registrationError", (e) => console.debug("push reg error", e));
    return () => { h1.remove(); h2.remove(); };
  } catch (err) {
    console.debug("push not available", err);
    return () => {};
  }
}
